import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Bot, CornerUpRight, FolderCheck, FolderInput, FolderPlus, Handshake, Loader2, Slack as SlackIcon, Sparkles,
  Reply as ReplyIcon, ExternalLink, AlertCircle,
} from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useSlack } from "@/contexts/SlackContext";
import { AttachmentPreviewDialog } from "@/pages/applications/inboxShared";
import { AttachmentsStrip, EmailBodyFrame } from "@/pages/inbox/ReadingPaneParts";
import { CATEGORY_TONE, HOW_LABEL, stageLabel } from "@/pages/inbox/CasePicker";
import { FlagChips, RoutingHints, StageChip, UnauthorisedHint } from "@/pages/inbox/TriageChips";
import { MoveToMenu } from "@/pages/inbox/MoveToMenu";
import { AccountsQuickActions } from "@/pages/inbox/AccountsQuickActions";
import { AssignMenu } from "@/pages/inbox/InboxActions";
import { PreReplyCard } from "@/pages/inbox/PreReplyCard";

const money = (v, cur = "£") => `${cur}${Number(v || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** The customer exists in LoanDisk but has no portal file yet (never been on the arrears report) — one click opens a
 *  Portfolio Account file and logs this email on it. */
function LoanDiskMatch({ matched, email, api, me, onTriaged, onFiled }) {
  const [busy, setBusy] = useState(false);
  const adopt = async () => {
    setBusy(true);
    try {
      const r = await api.adoptLoan(email.id);
      onTriaged(r.triage);
      onFiled?.(r.filed);
      toast.success(`Portfolio Account opened for ${r.case?.sf || r.case?.customer_name} — email logged on it`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); } finally { setBusy(false); }
  };
  const closed = matched.loan_status_id === 2 || /paid|closed|written/i.test(matched.loan_status || "");
  return (
    <>
      <span className="inline-flex items-center gap-1.5 text-[11px] border rounded px-2 py-1 bg-amber-50 border-amber-300 text-amber-900" data-testid="reading-pane-loandisk-match">
        <span className="font-semibold">LoanDisk</span>
        <span className="font-mono font-semibold">{matched.sf || "—"}</span>
        <span className="truncate max-w-[220px]" title={matched.customer_name}>{matched.customer_name}</span>
        {matched.borrower_name && <span className="text-amber-700 truncate max-w-[160px]">· {matched.borrower_name}</span>}
        <Badge variant="outline" className={cn("text-[9px] bg-white", closed ? "text-slate-600" : "text-emerald-700 border-emerald-300")}>{matched.loan_status || "loan"}</Badge>
        <span className="text-amber-800">{matched.balance_amount < 0 ? `overpaid ${money(-matched.balance_amount, api.currency)}` : `balance ${money(matched.balance_amount, api.currency)}`}</span>
        <span className="text-[10px] text-amber-700">{HOW_LABEL[matched.how] || ""} · no portal file yet</span>
      </span>
      <Button size="sm" variant="outline" className="h-6 text-[10px] text-amber-800 border-amber-400 bg-white hover:bg-amber-100" onClick={adopt} disabled={busy} data-testid="adopt-loan-btn"
        title="Open a Portfolio Account file for this customer and log this email on it">
        {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FolderPlus className="w-3 h-3 mr-1" />}Add account &amp; log
      </Button>
    </>
  );
}

export function DeptReadingPane({ email, api, me, title, caseLink, folders, currentFolder, onMove, onTriaged, onReply, onLogToCase, onForward, onMoveDone, onAssigned, onFiled }) {
  const [msg, setMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [classifying, setClassifying] = useState(false);
  const [atts, setAtts] = useState([]);
  const [previewAtt, setPreviewAtt] = useState(null);
  const [movingDone, setMovingDone] = useState(false);
  const selRef = useRef("");
  const slack = useSlack();
  const t = email.triage;
  const matched = t?.matched_case;

  useEffect(() => {
    let alive = true;
    setMsg(null); setAtts([]); selRef.current = "";
    (async () => {
      try {
        setLoading(true);
        const r = await api.getEmail(email.id);
        if (alive) setMsg(r.message);
      } catch (e) {
        if (alive) toast.error(e.response?.data?.detail || e.message);
      } finally { if (alive) setLoading(false); }
    })();
    if (email.has_attachments) api.attachments(email.id).then(r => { if (alive) setAtts(r.attachments || []); }).catch(() => {});
    // Dean (9 Oct): triage must not wait for the 2-min tick — an email opened before the tick got to it is triaged on the spot
    if (!email.triage) {
      setClassifying(true);
      api.classify(email.id).then(r => { if (alive) onTriaged(r.triage); }).catch(() => {}).finally(() => { if (alive) setClassifying(false); });
    }
    return () => { alive = false; };
  }, [email.id]);  // eslint-disable-line react-hooks/exhaustive-deps

  const classify = async () => {
    setClassifying(true);
    try {
      const r = await api.classify(email.id, true);
      onTriaged(r.triage);
    } catch (e) {
      toast.error("Triage failed: " + (e.response?.data?.detail || e.message));
    } finally { setClassifying(false); }
  };

  const snapToSlack = () => {
    if (!slack) return;
    let quote = selRef.current;
    if (!quote) {
      const tmp = document.createElement("div");
      tmp.innerHTML = msg?.body?.content || "";
      quote = (tmp.textContent || "").replace(/\s+/g, " ").trim().slice(0, 600);
    }
    const lines = [
      `:email: *Email snap — ${title}*`,
      `*From:* ${email.from_name || ""} <${email.from_email || ""}>`,
      `*Subject:* ${email.subject || "(no subject)"}`,
      email.received ? `*Received:* ${new Date(email.received).toLocaleString("en-GB")}` : null,
      t?.category ? `*AI triage:* ${t.category} (${t.confidence}%)${t.summary ? ` — ${t.summary}` : ""}` : null,
      matched ? `*Customer file:* ${matched.sf} ${matched.customer_name}` : null,
      "", ...quote.split("\n").filter(Boolean).map(l => `> ${l}`), "",
      "Any advice on how to handle this one?",
    ].filter(l => l !== null);
    slack.expandWithDraft(lines.join("\n"));
  };

  const filed = email.filed || [];

  return (
    <>
      <CardHeader className="pb-2 border-b">
        <div className="min-w-0">
          <CardTitle className="text-sm truncate" data-testid="reading-pane-subject">{email.subject || "(no subject)"}</CardTitle>
          <div className="text-xs text-muted-foreground mt-0.5 truncate" title={`${email.from_name || ""} <${email.from_email || ""}>`}>
            {email.from_name} &lt;{email.from_email}&gt; · {email.received ? new Date(email.received).toLocaleString("en-GB") : ""}
          </div>
          {!t && classifying && (
            <div className="mt-1.5 flex items-center gap-2 text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded px-2 py-1.5" data-testid="reading-pane-working">
              <Bot className="w-3 h-3 shrink-0" /><span className="font-medium">Working… AI triage in progress</span>
            </div>
          )}
          {t && (
            <div className="text-[11px] text-violet-800 bg-violet-50 border border-violet-200 rounded px-2 py-1 mt-1.5" data-testid="reading-pane-triage">
              <div className="flex items-start gap-1.5">
                <Bot className="w-3 h-3 mt-0.5 shrink-0" />
                <span>
                  <Badge variant="outline" className={cn("text-[9px] mr-1.5 align-middle", CATEGORY_TONE[t.category] || CATEGORY_TONE.Other)} data-testid="reading-pane-category">
                    {t.category} · {t.confidence}%
                  </Badge>
                  {t.needs_review && t.category !== "Needs review" && (
                    <Badge variant="outline" className="text-[9px] mr-1.5 align-middle bg-violet-100 text-violet-800 border-violet-300" title={`Below the confidence floor${t.second_choice ? ` — could also be ${t.second_choice}` : ""}`} data-testid="reading-pane-needs-review">needs review</Badge>
                  )}
                  {t.second_choice && t.category === "Needs review" && <span className="text-[10px] text-violet-700 mr-1.5">or {t.second_choice}</span>}
                  <span className="mr-1.5 align-middle"><StageChip stage={t.stage} /></span>
                  {t.summary}
                  {t.action_required && <span className="ml-1.5 font-semibold text-violet-900" data-testid="reading-pane-action">· Action: {t.action_required}</span>}
                </span>
              </div>
              {(t.flags?.length > 0 || t.authorised === false) && (
                <div className="mt-1 ml-[18px] flex flex-wrap items-center gap-1" data-testid="reading-pane-flags">
                  <FlagChips flags={t.flags} idKey="pane" max={8} />
                  <UnauthorisedHint triage={t} />
                </div>
              )}
              {t.note_summary && <div className="mt-1 ml-[18px] text-[10px] text-violet-700" title="The line written on the customer file when this email is logged" data-testid="reading-pane-note-summary">File note: {t.note_summary}</div>}
              <RoutingHints routing={t.routing} holdingOnly={t.holding_only} />
            </div>
          )}
          <div className="mt-1.5 flex items-center gap-2 flex-wrap" data-testid="reading-pane-case-row">
            {matched?.kind === "loandisk" ? (
              <LoanDiskMatch matched={matched} email={email} api={api} me={me} onTriaged={onTriaged} onFiled={onFiled} />
            ) : matched ? (
              <>
                <span className="inline-flex items-center gap-1.5 text-[11px] border rounded px-2 py-1 bg-indigo-50 border-indigo-200 text-indigo-900" data-testid="reading-pane-matched-case">
                  <span className="font-mono font-semibold">{matched.sf || "—"}</span>
                  <span className="truncate max-w-[260px]">{matched.customer_name}</span>
                  <Badge variant="outline" className="text-[9px] bg-white">{stageLabel(matched)}</Badge>
                  {matched.in_arrears && (
                    <Badge className="text-[9px] bg-red-600 hover:bg-red-600 text-white gap-1" data-testid="reading-pane-arrears-badge">
                      <AlertCircle className="w-2.5 h-2.5" />Arrears · {matched.days_past_due} dpd{matched.handler ? ` · ${matched.handler.split(" ")[0]}` : ""}
                    </Badge>
                  )}
                  <span className="text-[10px] text-indigo-600">{HOW_LABEL[matched.how] || ""}</span>
                </span>
                <Link to={caseLink(matched)} target="_blank" rel="noreferrer" data-testid="open-customer-file-btn">
                  <Button size="sm" variant="outline" className="h-6 text-[10px] text-indigo-700 border-indigo-300">
                    Open file<ExternalLink className="w-3 h-3 ml-1" />
                  </Button>
                </Link>
              </>
            ) : t ? (
              <span className="inline-flex items-center gap-1 text-[11px] text-amber-800" data-testid="reading-pane-no-case">
                <AlertCircle className="w-3 h-3" />No customer file matched — use “Log to file” to pick one
              </span>
            ) : null}
            {filed.map((f, i) => (
              <Badge key={i} variant="outline" className={cn("text-[9px] gap-0.5", f.kind === "p2p" ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-green-50 text-green-700 border-green-300")} data-testid={`reading-pane-filed-${i}`}>
                {f.kind === "p2p" ? <Handshake className="w-2.5 h-2.5" /> : <FolderCheck className="w-2.5 h-2.5" />}
                {f.kind === "reply" ? "reply logged" : f.kind === "p2p" ? "promise added" : "logged"} · {f.sf || f.customer_name}
              </Badge>
            ))}
          </div>
        </div>
      </CardHeader>

      <div className="flex flex-wrap items-center gap-1.5 px-4 py-2 border-b bg-slate-50" data-testid="email-action-bar">
        <Button size="sm" variant="default" className="h-7 text-[11px]" onClick={onReply} data-testid="reply-btn"
          title="Reply to the sender from this mailbox — optionally logged on the customer file">
          <ReplyIcon className="w-3 h-3 mr-1" />Reply
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-[11px] text-indigo-700 border-indigo-300" onClick={onLogToCase} data-testid="log-to-case-btn"
          title="Write this email onto a customer file's timeline">
          <FolderInput className="w-3 h-3 mr-1" />Log to file
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={onForward} data-testid="forward-email-btn">
          <CornerUpRight className="w-3 h-3 mr-1" />Forward
        </Button>
        <Button size="sm" variant="outline" className="h-7 text-[11px] text-slate-600" disabled={movingDone}
          onClick={async () => { setMovingDone(true); try { await onMoveDone(); } finally { setMovingDone(false); } }}
          data-testid="move-done-btn">
          {movingDone ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <FolderCheck className="w-3 h-3 mr-1" />}Move to done
        </Button>
        {onMove && <MoveToMenu folders={folders} current={currentFolder} onMove={onMove} />}
        <div className="ml-auto flex items-center gap-1">
          <AssignMenu email={email} api={api} me={me} onAssigned={onAssigned} />
          <Button size="sm" variant="outline" className="h-7 text-[11px] text-[#611f69] border-[#611f69]/30 hover:bg-[#611f69]/5"
            onClick={snapToSlack} disabled={!slack} title="Send a snap of this email to Slack (select text first to quote just that part)" data-testid="slack-snap-btn">
            <SlackIcon className="w-3 h-3 mr-1" />Slack
          </Button>
          <Button size="sm" variant="ghost" className="h-7 text-[11px] text-slate-500" onClick={classify} disabled={classifying} data-testid="classify-email-btn">
            {classifying ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1 text-violet-600" />}
            {t ? "Re-triage" : "AI triage"}
          </Button>
        </div>
      </div>

      {api.dept === "accounts" && matched && matched.case_id && (
        <div className="flex flex-wrap items-center gap-1.5 px-4 py-1.5 border-b bg-emerald-50/40" data-testid="accounts-quick-actions">
          <AccountsQuickActions matched={matched} email={email} api={api} me={me}
            onPromiseAdded={(r) => { if (r.triage) onTriaged(r.triage); onFiled?.(r.filed); onReply(r.reply_draft); }} />
        </div>
      )}
      {api.dept === "accounts" && <PreReplyCard email={email} api={api} matched={matched} onTriaged={onTriaged} onUseDraft={(draft) => onReply(draft)} />}

      <AttachmentsStrip atts={atts} messageId={email.id} onPreview={setPreviewAtt} fetchBlob={api.attachmentBlob} />
      <EmailBodyFrame html={msg?.body?.content} loading={loading} onSelection={(s) => { selRef.current = s; }} />
      {previewAtt && <AttachmentPreviewDialog messageId={email.id} att={previewAtt} onClose={() => setPreviewAtt(null)} fetchBlob={api.attachmentBlob} />}
    </>
  );
}
